import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { useMediaQuery, useTheme } from '@ltvco/refresh-lib/theme';
import { useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import {
  CALLER_ID_GB_FLAG_KEY,
  CALLER_ID_NOT_INTERESTED_STORAGE_KEY,
  CALLER_ID_NOTIFY_STORAGE_KEY,
  CALLER_ID_PRODUCT_GA,
  CALLER_ID_STICKY_DISMISSED_STORAGE_KEY,
  CALLER_ID_STICKY_UNITS,
  type CallerIdPageTypeGA,
  type CallerIdPlacementGA,
  type CallerIdUnitName,
} from './types';

export interface ICallerIdSmokeTestContext {
  pageType: CallerIdPageTypeGA;
  permalink: string;
  /** Spread into GA4 payloads for consistent `page_type` / `product` (RFRSH-4427). */
  gaEventBase: {
    page_type: CallerIdPageTypeGA;
    product: typeof CALLER_ID_PRODUCT_GA;
  };
  /** GB flag on and user has not opted out via notify or not interested (RFRSH-4427). */
  isExperienceActive: boolean;
  /** True when the bottom sticky CTA bar should render (RFRSH-4427). */
  isStickyBarVisible: boolean;
  isMobile: boolean;
  stickyUnit: CallerIdUnitName;
  openComingSoon: (
    unit: CallerIdUnitName,
    placement: CallerIdPlacementGA
  ) => void;
  closeModal: () => void;
  modalOpen: boolean;
  onNotifyClick: () => void;
  onNotInterestedClick: () => void;
  dismissStickyBar: () => void;
}

const CallerIdSmokeTestContext =
  createContext<ICallerIdSmokeTestContext | null>(null);

/**
 * Stable hash for sticky copy variant per permalink (RFRSH-4427).
 */
function hashPermalink(permalink: string): number {
  let h = 0;
  for (let i = 0; i < permalink.length; i += 1) {
    h = (Math.imul(31, h) + permalink.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

export interface ICallerIdSmokeTestProviderProps {
  pageType: CallerIdPageTypeGA;
  permalink: string;
  children: ReactNode;
}

function readNotifyPersisted(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return window.localStorage.getItem(CALLER_ID_NOTIFY_STORAGE_KEY) === '1';
}

function readNotInterestedPersisted(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return (
    window.localStorage.getItem(CALLER_ID_NOT_INTERESTED_STORAGE_KEY) === '1'
  );
}

function readStickyBarDismissed(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return (
    window.localStorage.getItem(CALLER_ID_STICKY_DISMISSED_STORAGE_KEY) === '1'
  );
}

/**
 * Local-only escape hatch: GrowthBook is swapped for an empty instance when
 * `user_info.email` is missing (see refresh-lib GrowthbookWrapper), so flags
 * never evaluate on. Set `VITE_CALLER_ID_SMOKE_DEV=true` in `.env.local` to
 * force this smoke test on in `npm start` without GB or a narrow viewport.
 */
function isCallerIdSmokeDevBypass(): boolean {
  if (!import.meta.env.DEV) {
    return false;
  }
  const raw = import.meta.env.VITE_CALLER_ID_SMOKE_DEV;
  const normalized = String(raw ?? '').toLowerCase();
  return normalized === 'true' || normalized === '1' || normalized === 'yes';
}

/**
 * Provides Smart Caller ID smoke-test state and GA wiring for mobile phone / fraud reports.
 */
export function CallerIdSmokeTestProvider({
  pageType,
  permalink,
  children,
}: ICallerIdSmokeTestProviderProps): JSX.Element {
  const theme = useTheme();
  // Match `ReportNavigationMobileMenu` (refresh-lib): visible below `lg`, hidden at `lg+`.
  // Using `down('md')` left a 900–1199px band where the bottom report nav shows but smoke CTAs/modal did not.
  const isMobileViewport = useMediaQuery(theme.breakpoints.down('lg'));
  const { trackEventGA4, logError } = useContext(AppConfig);
  const callerIdSmokeDevBypass = isCallerIdSmokeDevBypass();
  const isGbFromSdk = useFeatureIsOn(CALLER_ID_GB_FLAG_KEY);
  const isGbEnabled = callerIdSmokeDevBypass || isGbFromSdk;
  const isMobile = callerIdSmokeDevBypass || isMobileViewport;

  const [notifyPersisted, setNotifyPersisted] = useState(readNotifyPersisted);
  const [notInterestedPersisted, setNotInterestedPersisted] = useState(
    readNotInterestedPersisted
  );
  const [stickyBarDismissed, setStickyBarDismissed] = useState(
    readStickyBarDismissed
  );

  const isExperienceActive =
    isGbEnabled && !notifyPersisted && !notInterestedPersisted;

  const isStickyBarVisible =
    isExperienceActive && isMobile && !stickyBarDismissed;

  const baseEventPayload = useMemo(
    () => ({
      page_type: pageType,
      product: CALLER_ID_PRODUCT_GA,
    }),
    [pageType]
  );

  const stickyUnit: CallerIdUnitName =
    CALLER_ID_STICKY_UNITS[
      hashPermalink(permalink) % CALLER_ID_STICKY_UNITS.length
    ];

  const [modalOpen, setModalOpen] = useState(false);
  const [lastContext, setLastContext] = useState<{
    unit: CallerIdUnitName;
    placement: CallerIdPlacementGA;
  } | null>(null);

  const openComingSoon = useCallback(
    (unit: CallerIdUnitName, placement: CallerIdPlacementGA) => {
      if (!isExperienceActive) {
        return;
      }
      trackEventGA4({
        ...baseEventPayload,
        event_name: 'caller_id_cta_clicked',
        cta_variant: unit,
        placement,
      });
      trackEventGA4({
        ...baseEventPayload,
        event_name: 'caller_id_coming_soon_viewed',
        cta_variant: unit,
        placement,
      });
      setLastContext({ unit, placement });
      setModalOpen(true);
    },
    [baseEventPayload, isExperienceActive, trackEventGA4]
  );

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setLastContext(null);
  }, []);

  const onNotifyClick = useCallback(() => {
    if (!lastContext) {
      return;
    }
    trackEventGA4({
      ...baseEventPayload,
      event_name: 'caller_id_notify_clicked',
      cta_variant: lastContext.unit,
      placement: lastContext.placement,
    });
    try {
      window.localStorage.setItem(CALLER_ID_NOTIFY_STORAGE_KEY, '1');
    } catch (error: unknown) {
      logError(
        'caller_id_smoke_test_notify_localStorage',
        error instanceof Error ? error : new Error(String(error))
      );
    }
    setNotifyPersisted(true);
    setModalOpen(false);
  }, [baseEventPayload, lastContext, logError, trackEventGA4]);

  const onNotInterestedClick = useCallback(() => {
    if (!lastContext) {
      return;
    }
    trackEventGA4({
      ...baseEventPayload,
      event_name: 'caller_id_not_interested_clicked',
      cta_variant: lastContext.unit,
      placement: lastContext.placement,
    });
    try {
      window.localStorage.setItem(CALLER_ID_NOT_INTERESTED_STORAGE_KEY, '1');
    } catch (error: unknown) {
      logError(
        'caller_id_smoke_test_not_interested_localStorage',
        error instanceof Error ? error : new Error(String(error))
      );
    }
    setNotInterestedPersisted(true);
    setModalOpen(false);
    setLastContext(null);
  }, [baseEventPayload, lastContext, logError, trackEventGA4]);

  const dismissStickyBar = useCallback(() => {
    trackEventGA4({
      ...baseEventPayload,
      event_name: 'caller_id_sticky_bar_dismissed',
      cta_variant: stickyUnit,
      placement: 'sticky',
    });
    try {
      window.localStorage.setItem(CALLER_ID_STICKY_DISMISSED_STORAGE_KEY, '1');
    } catch (error: unknown) {
      logError(
        'caller_id_smoke_test_sticky_dismiss_localStorage',
        error instanceof Error ? error : new Error(String(error))
      );
    }
    setStickyBarDismissed(true);
  }, [baseEventPayload, logError, stickyUnit, trackEventGA4]);

  const value = useMemo(
    (): ICallerIdSmokeTestContext => ({
      pageType,
      permalink,
      gaEventBase: baseEventPayload,
      isExperienceActive,
      isStickyBarVisible,
      isMobile,
      stickyUnit,
      openComingSoon,
      closeModal,
      modalOpen,
      onNotifyClick,
      onNotInterestedClick,
      dismissStickyBar,
    }),
    [
      pageType,
      permalink,
      baseEventPayload,
      isExperienceActive,
      isStickyBarVisible,
      isMobile,
      stickyUnit,
      openComingSoon,
      closeModal,
      modalOpen,
      onNotifyClick,
      onNotInterestedClick,
      dismissStickyBar,
    ]
  );

  return (
    <CallerIdSmokeTestContext.Provider value={value}>
      {children}
    </CallerIdSmokeTestContext.Provider>
  );
}

export function useCallerIdSmokeTest(): ICallerIdSmokeTestContext {
  const ctx = useContext(CallerIdSmokeTestContext);
  if (!ctx) {
    throw new Error(
      'useCallerIdSmokeTest must be used within CallerIdSmokeTestProvider'
    );
  }
  return ctx;
}
